'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type * as UpChunk from '@mux/upchunk'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { Badge, Button, Input, Label, Progress } from '@mentora/ui-components'
import {
  AlertCircle,
  CircleCheck,
  Clock,
  Link2,
  Loader2,
  RefreshCw,
  Trash2,
  Upload,
  Video as VideoIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { useUploadFailureMessage } from '../video-upload/use-upload-failure-message'

type VideoStatus
  = | 'waiting_for_upload'
    | 'uploading'
    | 'asset_created'
    | 'ready'
    | 'errored'

interface InitialVideo {
  id: string
  status: VideoStatus
  durationSeconds: number | null
  originalFilename: string | null
  errorMessage: string | null
}

export interface LessonVideoUploaderActions {
  removeLessonVideo: (input: {
    lessonId: string
  }) => Promise<ActionResult>
}

export interface LessonVideoUploaderProps {
  lessonId: string
  initialVideo: InitialVideo | null
  /** The storefront's own server actions — the one thing the two copies of this screen never shared. */
  actions: LessonVideoUploaderActions
}

const ALLOWED_MIME_PREFIX = 'video/'
const MAX_SIZE_BYTES = 5 * 1024 * 1024 * 1024 // 5 GB
const POLL_INTERVAL_MS = 3000

const statusVariants: Record<VideoStatus, 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive'> = {
  waiting_for_upload: 'secondary',
  uploading: 'secondary',
  asset_created: 'secondary',
  ready: 'success',
  errored: 'destructive',
}

const statusIcons: Record<VideoStatus, { icon: typeof VideoIcon, spin?: boolean }> = {
  waiting_for_upload: { icon: Clock },
  uploading: { icon: Loader2, spin: true },
  asset_created: { icon: Loader2, spin: true },
  ready: { icon: CircleCheck },
  errored: { icon: AlertCircle },
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0)
    return '—'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  if (mins === 0)
    return `${secs}s`
  return `${mins}m ${secs.toString().padStart(2, '0')}s`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024)
    return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024)
    return `${kb.toFixed(1)} KB`
  const mb = kb / 1024
  if (mb < 1024)
    return `${mb.toFixed(1)} MB`
  return `${(mb / 1024).toFixed(2)} GB`
}

export function LessonVideoUploader({ lessonId, initialVideo, actions }: LessonVideoUploaderProps) {
  const router = useRouter()
  const t = useTranslations('courses.video')
  const uploadFailureMessage = useUploadFailureMessage()
  const { showError } = useActionToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const uploadRef = useRef<UpChunk.UpChunk | null>(null)
  const pollRef = useRef<number | null>(null)
  const videoIdRef = useRef<string | null>(initialVideo?.id ?? null)

  const [video, setVideo] = useState<InitialVideo | null>(initialVideo)
  const [progress, setProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [driveLink, setDriveLink] = useState('')
  const [driveOpen, setDriveOpen] = useState(false)
  const [importing, setImporting] = useState(false)
  const [isPending, startTransition] = useTransition()

  function driveRefusalMessage(code: string | undefined): string | null {
    switch (code) {
      case 'invalid_link': return t('drive.errors.invalidLink')
      case 'not_shared': return t('drive.errors.notShared')
      case 'not_a_video': return t('drive.errors.notAVideo')
      case 'unreachable': return t('drive.errors.unreachable')
      default: return null
    }
  }

  function statusLabel(status: VideoStatus): string {
    switch (status) {
      case 'waiting_for_upload': return t('status.waiting')
      case 'uploading': return t('status.uploading')
      case 'asset_created': return t('status.processing')
      case 'ready': return t('status.ready')
      case 'errored': return t('status.errored')
      default: return status
    }
  }

  function stopPolling() {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  function startPolling(videoId: string) {
    stopPolling()
    pollRef.current = window.setInterval(async () => {
      try {
        const res = await fetch(`/api/mux/video/${videoId}`, {
          cache: 'no-store',
        })
        if (!res.ok)
          return
        const data = (await res.json()) as InitialVideo & { status: VideoStatus }
        setVideo(data)
        if (data.status === 'ready' || data.status === 'errored') {
          stopPolling()
          router.refresh()
        }
      }
      catch {
        // swallow transient network errors; next tick will retry
      }
    }, POLL_INTERVAL_MS)
  }

  useEffect(() => {
    if (
      initialVideo
      && initialVideo.status !== 'ready'
      && initialVideo.status !== 'errored'
    ) {
      videoIdRef.current = initialVideo.id
      startPolling(initialVideo.id)
    }
    return () => {
      stopPolling()
      uploadRef.current?.abort()
    }
  }, [])

  async function handleFile(file: File) {
    if (!file.type.startsWith(ALLOWED_MIME_PREFIX)) {
      toast.error(t('errors.fileMustBeVideo'))
      return
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(t('errors.fileTooLarge', { size: formatBytes(MAX_SIZE_BYTES) }))
      return
    }

    setUploading(true)
    setProgress(0)

    let videoId: string
    let uploadUrl: string
    try {
      const res = await fetch('/api/mux/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lessonId,
          originalFilename: file.name,
          mimeType: file.type,
        }),
      })
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(err.error ?? t('errors.uploadRequestFailed'))
      }
      ;({ videoId, uploadUrl } = (await res.json()) as {
        videoId: string
        uploadUrl: string
      })
    }
    catch (err) {
      setUploading(false)
      toast.error(err instanceof Error ? err.message : t('errors.uploadFailed'))
      return
    }

    videoIdRef.current = videoId
    setVideo({
      id: videoId,
      status: 'uploading',
      durationSeconds: null,
      originalFilename: file.name,
      errorMessage: null,
    })

    const UpChunk = await import('@mux/upchunk')
    const chunker = UpChunk.createUpload({
      endpoint: uploadUrl,
      file,
      chunkSize: 30720,
    })
    uploadRef.current = chunker

    chunker.on('progress', (e: CustomEvent<number>) => {
      setProgress(Math.round(e.detail))
    })
    chunker.on('error', (e: CustomEvent<{ message: string }>) => {
      setUploading(false)
      setProgress(0)
      const message = uploadFailureMessage(e.detail.message, file)
      toast.error(message)
      setVideo(v =>
        v ? { ...v, status: 'errored', errorMessage: message } : v,
      )
    })
    chunker.on('success', () => {
      setUploading(false)
      setProgress(100)
      toast.success(t('uploadComplete'))
      setVideo(v => (v ? { ...v, status: 'asset_created' } : v))
      startPolling(videoId)
    })
  }

  // Mux fetches the file from Drive itself: nothing is uploaded from this
  // browser, so the row starts at `asset_created` and is polled like any other.
  async function onDriveImport(e: React.FormEvent) {
    e.preventDefault()
    const url = driveLink.trim()
    if (!url)
      return
    setImporting(true)
    try {
      const res = await fetch('/api/mux/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lessonId, url }),
      })
      const body = (await res.json().catch(() => ({}))) as {
        videoId?: string
        originalFilename?: string | null
        error?: string
        code?: string
      }
      if (!res.ok || !body.videoId) {
        toast.error(driveRefusalMessage(body.code) ?? body.error ?? t('drive.errors.failed'))
        return
      }
      videoIdRef.current = body.videoId
      setVideo({
        id: body.videoId,
        status: 'asset_created',
        durationSeconds: null,
        originalFilename: body.originalFilename ?? null,
        errorMessage: null,
      })
      setDriveLink('')
      setDriveOpen(false)
      toast.success(t('drive.started'))
      startPolling(body.videoId)
    }
    catch {
      toast.error(t('drive.errors.failed'))
    }
    finally {
      setImporting(false)
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file)
      void handleFile(file)
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file)
      void handleFile(file)
  }

  function onRemove() {
    startTransition(async () => {
      uploadRef.current?.abort()
      stopPolling()
      const result = await actions.removeLessonVideo({ lessonId })
      if (!result.ok) {
        showError(result)
        return
      }
      setVideo(null)
      videoIdRef.current = null
      setProgress(0)
      toast.success(t('videoRemoved'))
    })
  }

  const disabled = uploading || importing || isPending

  const driveForm = (
    <form onSubmit={onDriveImport} className="flex w-full flex-col gap-2 text-left">
      <Label htmlFor={`drive-link-${lessonId}`} className="text-sm">
        {t('drive.label')}
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id={`drive-link-${lessonId}`}
          type="url"
          inputMode="url"
          autoComplete="off"
          placeholder={t('drive.placeholder')}
          value={driveLink}
          onChange={e => setDriveLink(e.target.value)}
          disabled={disabled}
        />
        <Button type="submit" variant="outline" disabled={disabled || !driveLink.trim()}>
          {importing ? <Loader2 className="size-4 animate-spin" /> : <Link2 className="size-4" />}
          {importing ? t('drive.importing') : t('drive.import')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t('drive.hint')}</p>
    </form>
  )
  const meta = video ? { ...statusIcons[video.status], variant: statusVariants[video.status], label: statusLabel(video.status) } : null

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={onInputChange}
      />

      {video
        ? (
            <div className="space-y-3 rounded-lg border border-border/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {meta && (
                    <Badge variant={meta.variant}>
                      <meta.icon
                        className={meta.spin ? 'size-3 animate-spin' : 'size-3'}
                      />
                      {meta.label}
                    </Badge>
                  )}
                  {video.originalFilename && (
                    <span className="truncate text-xs text-muted-foreground">
                      {video.originalFilename}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatDuration(video.durationSeconds)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    onClick={() => inputRef.current?.click()}
                  >
                    <RefreshCw className="size-4" />
                    {t('replace')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    aria-expanded={driveOpen}
                    onClick={() => setDriveOpen(open => !open)}
                  >
                    <Link2 className="size-4" />
                    {t('drive.open')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    onClick={onRemove}
                  >
                    <Trash2 className="size-4" />
                    {t('remove')}
                  </Button>
                </div>
              </div>

              {driveOpen && (
                <div className="border-t border-border/60 pt-3">
                  {driveForm}
                </div>
              )}

              {uploading && (
                <div className="space-y-1.5">
                  <Progress value={progress} />
                  <p className="text-xs text-muted-foreground">
                    {t('uploadingProgress', { progress })}
                  </p>
                </div>
              )}

              {video.status === 'asset_created' && !uploading && (
                <p className="text-xs text-muted-foreground">
                  {t('muxProcessing')}
                </p>
              )}

              {video.status === 'errored' && video.errorMessage && (
                <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  {video.errorMessage}
                </p>
              )}
            </div>
          )
        : (
            <div
              onDragOver={(e) => {
                e.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={`flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-10 text-center transition-colors ${
                dragging
                  ? 'border-primary/60 bg-primary/5'
                  : 'border-border/60 bg-muted/20'
              }`}
            >
              <VideoIcon className="size-8 text-muted-foreground" />
              <div className="space-y-1">
                <p className="font-medium text-foreground">{t('drop')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('anyFormat', { size: formatBytes(MAX_SIZE_BYTES) })}
                </p>
              </div>
              <Button
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
              >
                <Upload className="size-4" />
                {t('chooseFile')}
              </Button>
              {uploading && (
                <div className="w-full max-w-xs space-y-1.5">
                  <Progress value={progress} />
                  <p className="text-xs text-muted-foreground">
                    {t('uploadingProgress', { progress })}
                  </p>
                </div>
              )}
              <div className="flex w-full max-w-md items-center gap-3 pt-2" aria-hidden>
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground uppercase">{t('drive.or')}</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <div className="w-full max-w-md">
                {driveForm}
              </div>
            </div>
          )}
    </div>
  )
}
